import { LatLngLiteral } from "leaflet";
import { clone, defaults, isEqual } from "lodash";

import ModelService from "../../redux/services/ModelService";
import SubscriptionPool from "../../redux/services/SubscriptionPool";
import sectors from "../../redux/reducers/sectors";
import { getPRPC } from "../../hooks/usePRPC";
import createRPCQuery from "../../utils/createRPCQuery.util";
import createLogger from "../../utils/logger.util";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { Language } from "..";

const logger = createLogger({ name: "Sector" });

class Sector extends ModelService<
	Sector.SubscribeOptions,
	Sector.Model,
	"sectors"
>(sectors, (state) => state.sectors) {
	private static request(
		callback: (prpc: any) => any,
		options: { silent?: boolean; verbose?: boolean; error?: boolean } = {},
	) {
		const prpc = getPRPC();
		if (!prpc) return null;
		return createRPCQuery(() => callback(prpc), options);
	}

	static defaultSharedOptions: Sector.SharedOptions = {
		deprecate: true,
	};

	static fromResponse(data: any): Sector.Model {
		const fn = (value) => {
			if (value?.type === "Polygon" && value?.coordinates?.length) {
				const coordinates: any[] = value.coordinates as any[];
				return (
					coordinates?.flat?.()?.map?.((v) => ({
						lat: v?.at?.(-1) || v?.lat,
						lng: v?.at?.(0) || v?.lng,
					})) || []
				);
			}

			if (Array.isArray(value) && value?.length) {
				return (
					value?.flat?.()?.map?.((v) => ({
						lat: v?.lat || v?.at?.(-1),
						lng: v?.lng || v?.at?.(0),
					})) || []
				);
			}

			return [];
		};

		const retval: Sector.Model = {
			id: data.id,

			taxiServiceId: data.taxiService.id,

			position: new Date(data.position),
			name: data.name,
			vertices: fn(data.vertices),
			isMaxSpeedEnabled: data.isMaxSpeedEnabled,
			maxSpeed: data.maxSpeed,

			active: data.active,
			availableForExecutor: data.availableForExecutor,
			isParking: data.isParking,
			isPriceZone: data.isPriceZone,

			isAverageSpeedEnabled: data.isAverageSpeedEnabled || false,
			averageSpeed: data.averageSpeed
				? data.averageSpeed?.map((item, i) => {
						const id = i + 1;
						return { ...(item || {}), id };
				  })
				: [],

			isBasicAverageSpeedEnabled:
				data.additionalFields?.isBasicAverageSpeedEnabled || false,
			basicAverageSpeed: data.basicAverageSpeed
				? data.basicAverageSpeed?.map((item, i) => {
						const id = i + 1;
						return { ...(item || {}), id };
				  })
				: [],

			createdAt: "",
			updatedAt: "",
			deletedAt: data?.deletedAt,
		};
		return retval;
	}

	static toRequest(
		model: Sector.Model.New | Sector.Model.Modified,
		quiet?: boolean,
	): any {
		const vertices = clone(model.vertices);

		if (vertices && !isEqual(vertices[vertices.length - 1], vertices[0]))
			vertices.push(vertices[0]);

		const averageSpeed = model.averageSpeed?.map(
			({ start, end, speed, dow }) => ({
				start,
				end,
				speed,
				dow,
			}),
		);

		const data = {
			taxiServiceId: model.taxiServiceId,

			position: model.position,
			name: model.name,
			vertices,
			isMaxSpeedEnabled: model.isMaxSpeedEnabled,
			maxSpeed: model.maxSpeed <= 0 ? 60 : model.maxSpeed,

			active: model.active,
			availableForExecutor: model.availableForExecutor,
			isParking: model.isParking,
			isPriceZone: model.isPriceZone,

			isAverageSpeedEnabled: model.isAverageSpeedEnabled || false,
			averageSpeed,
			additionalFields: {
				isBasicAverageSpeedEnabled:
					model.isBasicAverageSpeedEnabled || false,
			},
		};
		if (!quiet) {
			logger.info("[Sector] toRequest", { data });
		}

		return data;
	}

	static async store(
		object: Sector.Model.New,
		options?: Sector.StoreOptions,
	) {
		options = defaults(options, Sector.defaultSharedOptions);

		const prpc = getPRPC();

		if (!prpc) {
			logger.error(
				"[Sector] store: нет PRPC-сессии — sector.create на сервер не отправлялся",
			);
			throw new Error(
				"[Sector] store: WebSocket-сессия не готова (PRPC отсутствует)",
			);
		}

		logger.info("[Sector] store", object);

		await createRPCQuery(
			() => prpc.theirsModel.sector.create(Sector.toRequest(object)),
			{ silent: false },
		);

		if (options.deprecate) Sector.deprecateAll();
	}

	static async startImport(objects: Sector.Model.New[]) {
		const items = objects.map((o) => Sector.toRequest(o, true));

		return this.request(
			(prpc) => (prpc.theirsModel.sector as any).startImport({ items }),
			{
				silent: false,
				error: true,
			},
		);
	}

	static async getImportProgress() {
		const res = await this.request(
			(prpc) => (prpc.theirsModel.sector as any).getImportProgress(),
			{
				silent: true,
				error: true,
			},
		);

		return res;
	}

	static async cancelImport() {
		const res = await this.request(
			(prpc) => (prpc.theirsModel.sector as any).cancelImport(),
			{
				silent: false,
				error: true,
			},
		);

		return res;
	}

	/**
	 * Lightweight realtime subscription on sector changes (create/update/delete).
	 * Used to push live updates to the UI during long operations like import,
	 * without forcing a periodic refetch.
	 *
	 * Returns a teardown function. The pool layer takes care of reconnects
	 * across PRPC sessions and unsubscribe-on-disconnect.
	 */
	static async subscribeRealtime(
		params: { taxiServiceIds?: number[] },
		onChange: () => void,
	): Promise<() => void> {
		const prpc = getPRPC();

		if (!prpc) return () => undefined;

		try {
			const subscription = await SubscriptionPool.add(
				(session) =>
					session.theirsModel.sector.subscribe({
						params: {
							taxiServiceIds: params.taxiServiceIds,
						},
						ping: () => true,
						onEvent: () => {
							onChange();
						},
						onError: (error) => {
							logger.error(
								"[Sector] subscribeRealtime onError",
								error,
							);
						},
					}),
				{ name: "Sector.subscribeRealtime" },
			);

			return () => {
				subscription.unsubscribe().catch((error) => {
					logger.warn(
						"[Sector] subscribeRealtime unsubscribe failed",
						error,
					);
				});
			};
		} catch (error) {
			logger.error("[Sector] subscribeRealtime failed", error);
			return () => undefined;
		}
	}

	static async update(
		object: Sector.Model.Modified,
		options?: Sector.UpdateOptions,
	) {
		options = defaults(options, Sector.defaultSharedOptions);

		const prpc = getPRPC();

		if (!prpc) return;

		logger.info("[Sector] update", object);
		await createRPCQuery(() =>
			prpc.theirsModel.sector.update(object.id, Sector.toRequest(object)),
		);

		if (options.deprecate) Sector.deprecateAll();
	}

	static async destroy(
		id: number[] | number,
		options?: Sector.DestroyOptions,
	) {
		options = defaults(options, Sector.defaultSharedOptions);

		const prpc = getPRPC();

		if (!prpc) return;
		logger.info("[Sector] destroy", { id, options });
		if (Array.isArray(id))
			await Promise.all(id.map((id) => this.destroyOne(id)));
		else await this.destroyOne(id);

		if (options.deprecate) Sector.deprecateAll();
	}

	static async destroyOne(id: number) {
		const prpc = getPRPC();

		if (!prpc) return;

		const res = await createRPCQuery(() =>
			prpc.theirsModel.sector.delete(id),
		).catch((err) => {
			logger.error("[Sector] destroyOne", err);
		});
		logger.info("[Sector] destroyOne", { id, res });
	}

	static async getByPoint(point: LatLngLiteral) {
		const prpc = getPRPC();

		if (!prpc) return null;

		const [sector] = await createRPCQuery(() =>
			prpc?.theirsModel.sector.getByPoint(point),
		);

		return sector;
	}

	static async getAll(options: Sector.SubscribeOptions) {
		const prpc = getPRPC();

		if (!prpc) return [];
		try {
			const res = await createRPCQuery(() =>
				prpc?.theirsModel.sector.getAll(options),
			);

			if (!res) return [];
			logger.info("[Sector] getAll", res);
			return res.items?.map(Sector.fromResponse);
		} catch (error) {
			return [];
		}
	}

	static Global = {
		async index(options: Sector.SubscribeOptions) {
			const prpc = getPRPC();

			if (!prpc) return null;
			if (!prpc.theirsModel?.sector?.getAll) return null;

			const result = await createRPCQuery(() =>
				prpc.theirsModel.sector.getAll({
					limit: options.limit,
					offset: options.offset,
					query: options.query,
					taxiServiceIds: options.taxiServiceIds,
					active: options.active,
					availableForExecutor: options.availableForExecutor,
					isParking: options.isParking,
					lang: options.language,
					order: options.order,
				}),
			);

			const _result = {
				cache: result?.items?.map(Sector.fromResponse) || [],
				offset: result?.pagination?.offset || 0,
				limit: result?.pagination?.count || 0,
				total: result?.pagination?.count || 0,
				deprecated: false,
			};

			logger.info("[Sector] SubscribeOptions", result, _result);
			return _result;
		},
	};
}

declare namespace Sector {
	interface Model {
		id: number;

		taxiServiceId: number;

		position: Date | number;
		name: Record<Language, string>;
		vertices: LatLngLiteral[];
		isMaxSpeedEnabled: boolean;
		maxSpeed: number;

		active: boolean;
		availableForExecutor: boolean;
		isParking: boolean;
		isPriceZone: boolean;

		isAverageSpeedEnabled?: boolean;
		averageSpeed?: AverageSpeed[];
		isBasicAverageSpeedEnabled?: boolean;
		basicAverageSpeed?: AverageSpeed[];

		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	interface SubscribeOptions
		extends ServiceSubscribeOptionsBase<Sector.Model> {
		language?: Language;
		taxiServiceIds?: number[];

		active?: boolean;
		availableForExecutor?: boolean;
		isParking?: boolean;
	}

	interface SharedOptions {
		deprecate?: boolean;
	}

	interface ImportProgress {
		status: "running" | "completed" | "failed" | "cancelled";
		totalSectors: number;
		processedSectors: number;
		createdCount: number;
		failedCount: number;
		errors: { index: number; message: string }[];
		startedAt: string;
		updatedAt: string;
		finishedAt?: string;
	}

	interface AverageSpeed {
		// milliseconds (where 0 is the start of the day and 86400000 is the end of the day)
		start: number;
		end: number;
		// Days of the week (Sunday -- Saturday: 0 - 6)
		dow: number[];
		// km/h
		speed: number;
		id: number;
	}

	interface StoreOptions extends SharedOptions {}
	interface UpdateOptions extends SharedOptions {}
	interface DestroyOptions extends SharedOptions {}

	namespace Model {
		type New = Omit<Model, "id" | "createdAt" | "updatedAt" | "deletedAt">;
		type Modified = Omit<Model, "createdAt" | "updatedAt" | "deletedAt">;
	}
}

export default Sector;
